import { desdeIso, etiquetaDelDia } from "../../lib/calendario";

/**
 * El índice del calendario de entregas (ADR 0047).
 *
 * Vive aparte de la pantalla por dos razones: se recorre el listado **una vez**
 * en lugar de 42 —una por casilla— cada vez que alguien cambia de mes, y porque
 * es la regla que decide qué OT se ve en qué día, que es lo único de esta
 * pantalla que puede estar mal sin que se rompa nada.
 */

/** Lo mínimo que necesita el índice; el calendario le pasa la `OrdenListada`. */
interface ConEntrega {
  fechaPactadaEntrega: string | null;
}

/**
 * Las entregas por día, más cuántas quedaron afuera.
 *
 * La clave del mapa es el `getTime()` de la medianoche **local** del día, que
 * es lo que devuelve `desdeIso`: la misma aritmética que arma la grilla, así
 * que las dos coinciden sin comparar fechas casilla por casilla.
 *
 * `sinFecha` no es un detalle: una OT sin fecha pactada no cae en ninguna
 * casilla, y si no se la nombrara el calendario mostraría menos trabajo del que
 * hay pareciendo completo. Una fecha ilegible cuenta igual — nunca al día cero,
 * que sería una casilla real de 1970.
 */
export function agruparPorEntrega<T extends ConEntrega>(
  ordenes: readonly T[],
): { porDia: Map<number, T[]>; sinFecha: number } {
  const porDia = new Map<number, T[]>();
  let sinFecha = 0;

  for (const orden of ordenes) {
    const fecha = orden.fechaPactadaEntrega ? desdeIso(orden.fechaPactadaEntrega) : null;
    if (!fecha) {
      sinFecha += 1;
      continue;
    }
    const clave = fecha.getTime();
    const dia = porDia.get(clave);
    if (dia) dia.push(orden);
    else porDia.set(clave, [orden]);
  }

  return { porDia, sinFecha };
}

/**
 * El nombre accesible de una casilla del calendario.
 *
 * Lo visible es un número suelto y hasta tres recuadros, así que leído en voz
 * alta la casilla del 24 se anuncia «24» y nada más — ni qué mes, ni si hay
 * trabajo ahí. Y la casilla vacía **también se abre**, con lo cual sin esto no
 * habría forma de saber qué se está por abrir.
 */
export function etiquetaDeCasilla(fecha: Date, cantidad: number): string {
  const dia = etiquetaDelDia(fecha);
  if (cantidad === 0) return `${dia} — sin entregas`;
  return `${dia} — ${cantidad} ${cantidad === 1 ? "entrega" : "entregas"}`;
}
